import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  loadThaiAddressDatabase,
  isDatabaseAvailable,
  getLoadError,
  searchByField,
  getPostcodesForAddress,
  validateAddress,
  smartParseAddress,
  buildCleanAddress
} from '../utils/thaiAddressData';

/**
 * Thai Address Input Component with autocomplete and validation
 * 
 * Features:
 * - Autocomplete for subdistrict, district, province, postcode
 * - Cross-field validation
 * - Smart paste functionality to parse full address
 * - Auto-fill related fields when one is selected
 */
const ThaiAddressInput = ({
  // Address values
  address = '',
  subdistrict = '',
  district = '',
  province = '',
  postcode = '',
  name = '',
  phone = '',
  // Change handlers
  onAddressChange,
  onSubdistrictChange,
  onDistrictChange,
  onProvinceChange,
  onPostcodeChange,
  onNameChange,
  onPhoneChange,
  // Optional: callback when all values change at once (from parsing)
  onAllFieldsChange,
  // Styling
  className = '',
  inputClassName = 'w-full p-3 border border-gray-300 rounded-lg text-lg',
  labelClassName = 'block text-sm font-medium mb-1',
  // Show/hide fields
  showName = true,
  showPhone = true,
  showAddress = true,
  // Validation
  showValidation = true,
  // Hide paste button
  hidePasteButton = false,
}) => {
  const [isDbLoading, setIsDbLoading] = useState(true);
  const [isDbAvailable, setIsDbAvailable] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [activeField, setActiveField] = useState(null);
  const [validationResult, setValidationResult] = useState({ isValid: true, errors: [], databaseUnavailable: true });
  const [showPasteModal, setShowPasteModal] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [parsedPreview, setParsedPreview] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  
  const suggestionRef = useRef(null);
  const inputRefs = {
    subdistrict: useRef(null),
    district: useRef(null),
    province: useRef(null),
    postcode: useRef(null),
  };

  // Load database on mount
  useEffect(() => {
    loadThaiAddressDatabase()
      .then((data) => {
        const available = isDatabaseAvailable();
        console.log('Database load result:', { dataLength: data?.length, available, error: getLoadError() });
        setIsDbLoading(false);
        setIsDbAvailable(available);
      })
      .catch((error) => {
        console.error('Error in loadThaiAddressDatabase:', error);
        setIsDbLoading(false);
        setIsDbAvailable(false);
      });
  }, []);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (suggestionRef.current && !suggestionRef.current.contains(event.target)) {
        setSuggestions([]);
        setActiveField(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Validate address when values change
  useEffect(() => {
    if (isDbLoading) return;
    
    // Only validate if we have at least 2 fields filled
    const filledFields = [subdistrict, district, province, postcode].filter(f => f && f.trim()).length;
    if (filledFields < 2) {
      setValidationResult({ isValid: true, errors: [], databaseUnavailable: !isDbAvailable });
      return;
    }

    const validation = validateAddress({ subdistrict, district, province, postcode });
    setValidationResult(validation);
  }, [isDbLoading, isDbAvailable, subdistrict, district, province, postcode]);

  // Handle input change with autocomplete
  const handleFieldChange = useCallback((field, value) => {
    // Update the field value
    switch (field) {
      case 'subdistrict':
        onSubdistrictChange && onSubdistrictChange(value);
        break;
      case 'district':
        onDistrictChange && onDistrictChange(value);
        break;
      case 'province':
        onProvinceChange && onProvinceChange(value);
        break;
      case 'postcode':
        onPostcodeChange && onPostcodeChange(value);
        break;
      default:
        break;
    }

    // Search for suggestions (only if database is available)
    if (value && value.length >= 1 && isDbAvailable) {
      let results;
      if (field === 'postcode' && subdistrict && district && province) {
        // Same subdistrict/district/province can have multiple postcodes: show matching options
        const postcodes = getPostcodesForAddress(subdistrict, district, province);
        const v = value.toString().trim();
        const filtered = v ? postcodes.filter(p => p.startsWith(v)) : postcodes;
        results = filtered.slice(0, 10).map(p => ({ subdistrict, district, province, postcode: p }));
      } else {
        results = searchByField(value, field, 10);
      }
      setSuggestions(results);
      setActiveField(field);
    } else {
      setSuggestions([]);
      setActiveField(null);
    }
  }, [isDbAvailable, onSubdistrictChange, onDistrictChange, onProvinceChange, onPostcodeChange, subdistrict, district, province]);

  // Handle suggestion selection
  const handleSelectSuggestion = useCallback((suggestion) => {
    // Update all related fields
    if (onSubdistrictChange) onSubdistrictChange(suggestion.subdistrict || '');
    if (onDistrictChange) onDistrictChange(suggestion.district || '');
    if (onProvinceChange) onProvinceChange(suggestion.province || '');
    if (onPostcodeChange) onPostcodeChange(suggestion.postcode?.toString() || '');

    // If onAllFieldsChange is provided, call it too
    if (onAllFieldsChange) {
      onAllFieldsChange({
        subdistrict: suggestion.subdistrict || '',
        district: suggestion.district || '',
        province: suggestion.province || '',
        postcode: suggestion.postcode?.toString() || '',
      });
    }

    setSuggestions([]);
    setActiveField(null);
  }, [onSubdistrictChange, onDistrictChange, onProvinceChange, onPostcodeChange, onAllFieldsChange]);

  // Handle paste button click
  const handlePasteButtonClick = () => {
    setShowPasteModal(true);
    setPasteText('');
    setParsedPreview(null);
    setSelectedLocation(null);
  };

  // Handle paste text change
  const handlePasteTextChange = (e) => {
    const text = e.target.value;
    setPasteText(text);
    setSelectedLocation(null);
    
    if (text.trim()) {
      const parsed = smartParseAddress(text);
      setParsedPreview(parsed);
      // Auto-select if exact match found
      if (parsed.exactMatch) {
        setSelectedLocation(parsed.exactMatch);
      }
    } else {
      setParsedPreview(null);
    }
  };

  // Handle location selection from candidates
  const handleSelectLocation = (location) => {
    setSelectedLocation(location);
  };

  // Apply parsed address
  const handleApplyParsed = () => {
    if (!parsedPreview) return;

    // Use selected location
    const locationData = selectedLocation || {};
    
    // Build clean address based on selected location
    const cleanAddress = buildCleanAddress(
      parsedPreview.rawText || pasteText,
      { name: parsedPreview.name, phone: parsedPreview.phone, postcode: parsedPreview.postcode },
      selectedLocation
    );
    
    // Apply name
    if (showName && onNameChange && parsedPreview.name) {
      onNameChange(parsedPreview.name);
    }
    
    // Apply phone
    if (showPhone && onPhoneChange && parsedPreview.phone) {
      onPhoneChange(parsedPreview.phone);
    }
    
    // Apply clean address
    if (showAddress && onAddressChange) {
      onAddressChange(cleanAddress);
    }
    
    // Apply location data from selection
    if (onSubdistrictChange) onSubdistrictChange(locationData.subdistrict || '');
    if (onDistrictChange) onDistrictChange(locationData.district || '');
    if (onProvinceChange) onProvinceChange(locationData.province || '');
    if (onPostcodeChange) onPostcodeChange(locationData.postcode || parsedPreview.postcode || '');

    if (onAllFieldsChange) {
      onAllFieldsChange({
        name: parsedPreview.name || '',
        phone: parsedPreview.phone || '',
        address: cleanAddress,
        subdistrict: locationData.subdistrict || '',
        district: locationData.district || '',
        province: locationData.province || '',
        postcode: locationData.postcode || parsedPreview.postcode || '',
      });
    }

    setShowPasteModal(false);
    setPasteText('');
    setParsedPreview(null);
    setSelectedLocation(null);
  };

  // Check if can submit
  const canApply = parsedPreview && (
    selectedLocation || // Has selected a location
    (!parsedPreview.hasPostcode && parsedPreview.candidates?.length === 0) // No postcode, allow manual entry
  );

  // Render suggestion dropdown
  const renderSuggestions = (field) => {
    if (activeField !== field || suggestions.length === 0) return null;

    return (
      <div
        ref={suggestionRef}
        className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto"
      >
        {suggestions.map((suggestion, index) => (
          <div
            key={index}
            className="px-4 py-2 cursor-pointer hover:bg-blue-50 border-b border-gray-100 last:border-b-0"
            onClick={() => handleSelectSuggestion(suggestion)}
          >
            <div className="text-sm font-medium">
              {suggestion.subdistrict} » {suggestion.district}
            </div>
            <div className="text-xs text-gray-500">
              {suggestion.province} {suggestion.postcode}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className={`thai-address-input space-y-4 ${className}`}>
      {/* Smart Paste Button */}
      {!hidePasteButton && (
        <div className="flex justify-end mb-2">
          <button
            type="button"
            onClick={handlePasteButtonClick}
            className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 flex items-center gap-2 text-sm"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            วางที่อยู่แบบเต็ม
          </button>
        </div>
      )}

      {/* Name and Phone */}
      {(showName || showPhone) && (
        <div className="grid grid-cols-2 gap-4">
          {showName && (
            <div>
              <label className={labelClassName}>ชื่อผู้รับ *</label>
              <input
                type="text"
                value={name}
                onChange={(e) => onNameChange && onNameChange(e.target.value)}
                placeholder="กรอกชื่อผู้รับ"
                className={inputClassName}
              />
            </div>
          )}
          {showPhone && (
            <div>
              <label className={labelClassName}>เบอร์โทร *</label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => onPhoneChange && onPhoneChange(e.target.value)}
                placeholder="กรอกเบอร์โทร"
                className={inputClassName}
              />
            </div>
          )}
        </div>
      )}

      {/* Street Address */}
      {showAddress && (
        <div>
          <label className={labelClassName}>บ้านเลขที่ ถนน ซอย ชื่อสถานที่</label>
          <textarea
            value={address}
            onChange={(e) => onAddressChange && onAddressChange(e.target.value)}
            placeholder="กรอกที่อยู่สำหรับจัดส่ง"
            rows={2}
            className={`${inputClassName} resize-none`}
          />
        </div>
      )}

      {/* Subdistrict with autocomplete */}
      <div className="relative">
        <label className={labelClassName}>ตำบล/แขวง *</label>
        <input
          ref={inputRefs.subdistrict}
          type="text"
          value={subdistrict}
          onChange={(e) => handleFieldChange('subdistrict', e.target.value)}
          onFocus={() => {
            if (subdistrict && isDbAvailable) {
              const results = searchByField(subdistrict, 'subdistrict', 10);
              setSuggestions(results);
              setActiveField('subdistrict');
            }
          }}
          placeholder="พิมพ์ชื่อตำบล/แขวง"
          className={inputClassName}
          autoComplete="off"
        />
        {renderSuggestions('subdistrict')}
      </div>

      {/* District with autocomplete */}
      <div className="relative">
        <label className={labelClassName}>อำเภอ/เขต *</label>
        <input
          ref={inputRefs.district}
          type="text"
          value={district}
          onChange={(e) => handleFieldChange('district', e.target.value)}
          onFocus={() => {
            if (district && isDbAvailable) {
              const results = searchByField(district, 'district', 10);
              setSuggestions(results);
              setActiveField('district');
            }
          }}
          placeholder="พิมพ์ชื่ออำเภอ/เขต"
          className={inputClassName}
          autoComplete="off"
        />
        {renderSuggestions('district')}
      </div>

      {/* Province with autocomplete */}
      <div className="relative">
        <label className={labelClassName}>จังหวัด *</label>
        <input
          ref={inputRefs.province}
          type="text"
          value={province}
          onChange={(e) => handleFieldChange('province', e.target.value)}
          onFocus={() => {
            if (province && isDbAvailable) {
              const results = searchByField(province, 'province', 10);
              setSuggestions(results);
              setActiveField('province');
            }
          }}
          placeholder="พิมพ์ชื่อจังหวัด"
          className={inputClassName}
          autoComplete="off"
        />
        {renderSuggestions('province')}
      </div>

      {/* Postcode with autocomplete */}
      <div className="relative">
        <label className={labelClassName}>รหัสไปรษณีย์ *</label>
        <input
          ref={inputRefs.postcode}
          type="text"
          value={postcode}
          onChange={(e) => handleFieldChange('postcode', e.target.value)}
          onFocus={() => {
            if (isDbAvailable) {
              if (subdistrict && district && province) {
                const postcodes = getPostcodesForAddress(subdistrict, district, province);
                const results = postcodes.map(p => ({ subdistrict, district, province, postcode: p }));
                setSuggestions(results);
              } else if (postcode) {
                const results = searchByField(postcode, 'postcode', 10);
                setSuggestions(results);
              }
              setActiveField('postcode');
            }
          }}
          placeholder="กรอกรหัสไปรษณีย์"
          className={inputClassName}
          autoComplete="off"
        />
        {renderSuggestions('postcode')}
      </div>

      {/* Database unavailable notice */}
      {showValidation && !isDbLoading && !isDbAvailable && (
        <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex items-center gap-2 text-yellow-700 font-medium">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            ไม่สามารถโหลดฐานข้อมูลที่อยู่ได้
          </div>
          <p className="text-sm text-yellow-600 mt-1">
            ระบบตรวจสอบที่อยู่และ autocomplete ไม่พร้อมใช้งาน กรุณากรอกที่อยู่ด้วยตนเอง
          </p>
        </div>
      )}

      {/* Validation Errors */}
      {showValidation && !validationResult.databaseUnavailable && validationResult.errors.length > 0 && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center gap-2 text-red-700 font-medium mb-1">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            พบปัญหาที่อยู่
          </div>
          <ul className="list-disc list-inside text-sm text-red-600">
            {validationResult.errors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Success indicator when valid (only show if database is available and validation passed) */}
      {showValidation && !validationResult.databaseUnavailable && validationResult.errors.length === 0 && subdistrict && district && province && postcode && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center gap-2 text-green-700 font-medium">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            ที่อยู่ถูกต้อง
          </div>
        </div>
      )}

      {/* Paste Modal */}
      {showPasteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">วางที่อยู่แบบเต็ม</h3>
              <button
                onClick={() => setShowPasteModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <p className="text-gray-600 mb-4 text-sm">
              วางที่อยู่ทั้งหมดในช่องด้านล่าง ระบบจะค้นหาตำบล/อำเภอ/จังหวัด จากรหัสไปรษณีย์
            </p>

            <textarea
              value={pasteText}
              onChange={handlePasteTextChange}
              placeholder="วางที่อยู่ที่นี่ เช่น: สมชาย ใจดี 0812345678 123 ม.1 ต.ในเมือง อ.เมือง จ.ขอนแก่น 40000"
              rows={4}
              className="w-full p-3 border border-gray-300 rounded-lg text-base mb-4 resize-none"
              autoFocus
            />

            {/* Extracted basic info (name, phone, postcode) */}
            {parsedPreview && (
              <div className="bg-blue-50 p-3 rounded-lg mb-4">
                <h4 className="text-sm font-medium text-blue-800 mb-2">ข้อมูลที่ดึงได้:</h4>
                <div className="grid grid-cols-3 gap-3 text-sm">
                  <div>
                    <span className="text-gray-600">ชื่อ:</span>
                    <span className="ml-2 font-medium text-blue-700">{parsedPreview.name || '-'}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">เบอร์โทร:</span>
                    <span className="ml-2 font-medium text-blue-700">{parsedPreview.phone || '-'}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">รหัสไปรษณีย์:</span>
                    <span className="ml-2 font-medium text-blue-700">{parsedPreview.postcode || '-'}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Location selection - REQUIRED */}
            {parsedPreview?.candidates?.length > 0 && (
              <div className="mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <h4 className="font-medium text-gray-700">
                    {parsedPreview.exactMatch 
                      ? '✓ พบที่อยู่ที่ตรงกัน'
                      : '⚠️ กรุณาเลือกที่อยู่ที่ถูกต้อง'
                    }
                  </h4>
                  {!parsedPreview.exactMatch && parsedPreview.needsSelection && (
                    <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded">
                      * ต้องเลือก
                    </span>
                  )}
                </div>

                {/* Show as buttons/cards */}
                <div className="flex flex-wrap gap-2">
                  {parsedPreview.candidates.slice(0, 6).map((loc, index) => {
                    const isSelected = selectedLocation === loc;
                    const isMatch = loc.matchedParts?.length >= 3;
                    
                    return (
                      <button
                        key={index}
                        type="button"
                        onClick={() => handleSelectLocation(loc)}
                        className={`px-3 py-2 rounded-lg border-2 text-left transition-all ${
                          isSelected
                            ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
                            : isMatch
                            ? 'border-green-300 bg-green-50 hover:border-green-400'
                            : 'border-gray-200 bg-white hover:border-gray-400'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <div>
                            <div className="font-medium text-sm">{loc.subdistrict}</div>
                            <div className="text-xs text-gray-600">
                              {loc.district}, {loc.province}
                            </div>
                          </div>
                          {isSelected && (
                            <svg className="w-5 h-5 text-blue-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                          )}
                        </div>
                        {/* Show matched parts */}
                        {loc.matchedParts?.length > 0 && (
                          <div className="mt-1">
                            <span className={`text-xs px-1.5 py-0.5 rounded ${
                              loc.matchedParts.length >= 3 ? 'bg-green-100 text-green-700' :
                              loc.matchedParts.length >= 2 ? 'bg-yellow-100 text-yellow-700' :
                              'bg-gray-100 text-gray-600'
                            }`}>
                              ตรงกับ: {loc.matchedParts.join(', ')}
                            </span>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
                
                {parsedPreview.candidates.length > 6 && (
                  <p className="text-xs text-gray-500 mt-2">
                    + อีก {parsedPreview.candidates.length - 6} ตัวเลือก
                  </p>
                )}
              </div>
            )}

            {/* Selected location preview */}
            {selectedLocation && (
              <div className="bg-green-50 p-3 rounded-lg mb-4 border border-green-200">
                <h4 className="font-medium text-green-700 mb-2 flex items-center gap-2">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  ที่อยู่ที่จะนำเข้า:
                </h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="text-gray-500">ตำบล/แขวง:</span> <span className="font-medium">{selectedLocation.subdistrict}</span></div>
                  <div><span className="text-gray-500">อำเภอ/เขต:</span> <span className="font-medium">{selectedLocation.district}</span></div>
                  <div><span className="text-gray-500">จังหวัด:</span> <span className="font-medium">{selectedLocation.province}</span></div>
                  <div><span className="text-gray-500">รหัสไปรษณีย์:</span> <span className="font-medium">{selectedLocation.postcode}</span></div>
                </div>
              </div>
            )}

            {/* Invalid postcode */}
            {parsedPreview?.invalidPostcode && (
              <div className="bg-red-50 p-3 rounded-lg mb-4 border border-red-200">
                <div className="flex items-center gap-2 text-red-700">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <span className="font-medium">รหัสไปรษณีย์ {parsedPreview.postcode} ไม่ถูกต้อง</span>
                </div>
                <p className="text-sm text-red-600 mt-1">กรุณาตรวจสอบรหัสไปรษณีย์อีกครั้ง</p>
              </div>
            )}

            {/* No postcode found */}
            {parsedPreview && !parsedPreview.postcode && (
              <div className="bg-yellow-50 p-3 rounded-lg mb-4 border border-yellow-200">
                <div className="flex items-center gap-2 text-yellow-700">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <span className="font-medium">ไม่พบรหัสไปรษณีย์</span>
                </div>
                <p className="text-sm text-yellow-600 mt-1">กรุณาใส่รหัสไปรษณีย์ 5 หลักในที่อยู่ เช่น 10110, 40000</p>
              </div>
            )}

            {/* Warning when selection required */}
            {parsedPreview?.needsSelection && !selectedLocation && (
              <div className="bg-orange-50 p-3 rounded-lg mb-4 border border-orange-200">
                <div className="flex items-center gap-2 text-orange-700">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <span className="font-medium">กรุณาเลือกที่อยู่จากรายการด้านบนก่อนนำเข้า</span>
                </div>
              </div>
            )}

            <div className="flex gap-3 justify-end mt-4">
              <button
                onClick={() => setShowPasteModal(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                ยกเลิก
              </button>
              <button
                onClick={handleApplyParsed}
                disabled={!canApply}
                className={`px-4 py-2 rounded-lg flex items-center gap-2 ${
                  canApply
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                นำเข้าที่อยู่
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Loading indicator */}
      {isDbLoading && (
        <div className="text-sm text-gray-500 text-center">
          กำลังโหลดฐานข้อมูลที่อยู่...
        </div>
      )}
    </div>
  );
};

export default ThaiAddressInput;

